import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Grid, Container, List, ListItem, ListItemText,
    useTheme, useMediaQuery, IconButton, Tabs, Tab, Button,
    TextField, InputAdornment, CircularProgress, Tooltip,
    ListItemAvatar, Avatar, Snackbar, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Receipt, Search, Download, FileText, AlertTriangle,
    CreditCard, RefreshCw, Check, Clock, X, Menu, ExternalLink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import '../MyProfile.css';
import './Payment.css';
import { useAuth0 } from '@auth0/auth0-react';

// Import the payment service with the correct path
import { paymentService } from '../../../services/paymentService';

// Import tab components
import PaymentMethods from './PaymentMethods';
import Subscriptions from './Subscriptions';
import Sidebar from '../Sidebar';

// Background Animated Blobs
const BackgroundBlobs = ({ darkMode }) => {
    return (
        <div className="background-blobs">
            <div className="blob payment-blob-1" style={{
                opacity: darkMode ? 0.12 : 0.2,
                background: 'radial-gradient(circle, rgba(255, 71, 117, 0.6), rgba(255, 71, 117, 0))'
            }}></div>
            <div className="blob payment-blob-2" style={{
                opacity: darkMode ? 0.12 : 0.2,
                background: 'radial-gradient(circle, rgba(58, 204, 225, 0.5), rgba(58, 204, 225, 0))'
            }}></div>
            <div className="blob payment-blob-3" style={{
                opacity: darkMode ? 0.1 : 0.15,
                background: 'radial-gradient(circle, rgba(121, 80, 242, 0.4), rgba(121, 80, 242, 0))'
            }}></div>
        </div>
    );
};

// Glass Card Component for consistent styling
const GlassCard = ({ children, darkMode }) => {
    return (
        <Box sx={{ 
            borderRadius: '24px', 
            overflow: 'hidden', 
            background: darkMode ? 'rgba(32, 33, 44, 0.35)' : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.25)' : '0 10px 30px rgba(0, 0, 0, 0.1)'
        }}>
            {children}
        </Box>
    );
};

// Animated Status Badge component
const AnimatedStatusBadge = ({ status }) => {
    // Get display status and map to UI-friendly version
    const getStatusDisplay = (stripeStatus) => {
        switch (stripeStatus) {
            case 'paid':
                return 'Paid';
            case 'draft':
            case 'open':
                return 'Open';
            case 'uncollectible':
            case 'failed':
                return 'Failed';
            case 'canceling':
                return 'Canceling';
            default:
                return stripeStatus.charAt(0).toUpperCase() + stripeStatus.slice(1);
        }
    };

    const displayStatus = getStatusDisplay(status);
    
    const getStatusColor = () => {
        switch (displayStatus) {
            case 'Paid': return '#4CAF50';
            case 'Open': 
            case 'Pending': return '#FFC107';
            case 'Failed': return '#F44336';
            case 'Canceling': return '#FF9800';
            default: return '#FFC107';
        }
    };

    const getStatusIcon = () => {
        switch (displayStatus) {
            case 'Paid': return <Check size={12} />;
            case 'Open': 
            case 'Pending': return <Clock size={12} />;
            case 'Failed': return <X size={12} />;
            case 'Canceling': return <RefreshCw size={12} />;
            default: return <Clock size={12} />;
        }
    };

    return (
        <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
                type: "spring",
                stiffness: 500,
                damping: 30
            }}
            style={{
                backgroundColor: `${getStatusColor()}15`,
                color: getStatusColor(),
                padding: '4px 8px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'capitalize'
            }}
        >
            {getStatusIcon()}
            <span>{displayStatus}</span>
        </motion.div>
    );
};

// Custom styled download button for better visibility
const DownloadButton = styled(IconButton)(({ theme, disabled, darkMode }) => ({
    backgroundColor: darkMode ? 'rgba(255, 71, 117, 0.15)' : 'rgba(255, 71, 117, 0.08)',
    color: darkMode ? 'white' : '#FF4775',
    width: 32,
    height: 32,
    borderRadius: '8px',
    '&:hover': {
        backgroundColor: darkMode ? 'rgba(255, 71, 117, 0.25)' : 'rgba(255, 71, 117, 0.15)',
    },
    '&.Mui-disabled': {
        backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    }
}));

// Main Payment Component
const Payment = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [darkMode, setDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePaymentFilter, setActivePaymentFilter] = useState('all');
    const { getAccessTokenSilently } = useAuth0();
    
    // Track loading state for each invoice separately
    const [downloadingInvoices, setDownloadingInvoices] = useState({});
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
    
    // Add state for sidebar expansion - using isOpen as expected by the Sidebar component
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

    // State for invoices
    const [invoices, setInvoices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // UPDATED: Fetch invoices with payment service
    const fetchInvoices = async () => {
        try {
            setLoading(true);
            
            // Get the auth token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            // Call the payment service with authentication
            const response = await paymentService.getInvoices(token);
            console.log('Invoices response:', response.data);
            
            // Set the invoices array
            setInvoices(response.data || []);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError(err.friendlyMessage || 'Failed to fetch invoices. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // UPDATED: Handle downloading invoice using payment service
    const handleDownloadInvoice = async (invoice) => {
        try {
            // Set loading state for this specific invoice only
            setDownloadingInvoices(prev => ({ ...prev, [invoice.order_number]: true }));
            
            // Show notification that download is preparing
            setNotification({
                open: true,
                message: 'Preparing invoice...',
                severity: 'info'
            });
            
            // Get auth token
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            // Check if we have a direct download URL
            if (invoice.hosted_invoice_url) {
                // Open the hosted invoice URL in a new tab
                window.open(invoice.hosted_invoice_url, '_blank');
                
                // Show success notification with instructions
                setNotification({
                    open: true,
                    message: `Invoice #${invoice.order_number || ''} opened. Use the download button on the Stripe page to save it.`,
                    severity: 'success'
                });
            }
            // Additional direct PDF link if available
            else if (invoice.invoice_pdf) {
                window.open(invoice.invoice_pdf, '_blank');
                
                setNotification({
                    open: true,
                    message: `Invoice PDF opened in a new tab.`,
                    severity: 'success'
                });
            }
            // Fallback to API call if no direct URL is found
            else {
                const response = await paymentService.downloadInvoice(invoice.id, token);
                const invoiceData = response.data;
                
                // Handle response from API
                if (invoiceData.hosted_invoice_url) {
                    window.open(invoiceData.hosted_invoice_url, '_blank');
                    
                    setNotification({
                        open: true,
                        message: `Invoice opened. Use the download button on the Stripe page to save it.`,
                        severity: 'success'
                    });
                }
                else if (invoiceData.portal_url) {
                    window.open(invoiceData.portal_url, '_blank');
                    
                    setNotification({
                        open: true,
                        message: 'All your invoices are available in the Stripe customer portal',
                        severity: 'info'
                    });
                }
                else {
                    setNotification({
                        open: true,
                        message: invoiceData.message || 'Invoice not available',
                        severity: 'error'
                    });
                }
            }
        } catch (error) {
            console.error('Error handling invoice download:', error);
            
            setNotification({
                open: true,
                message: error.friendlyMessage || 'Failed to retrieve invoice',
                severity: 'error'
            });
        } finally {
            // Clear loading state for this specific invoice only
            setDownloadingInvoices(prev => ({ ...prev, [invoice.order_number]: false }));
        }
    };

    // Handle close notification
    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    // Fetch invoices on component mount
    useEffect(() => {
        fetchInvoices();
        
        // Initialize sidebar open state based on screen size
        setSidebarOpen(!isMobile);
    }, [isMobile]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    const handlePaymentFilterChange = (filter) => {
        setActivePaymentFilter(filter);
    };

    // Filtered invoices based on active filter and search query
    const filteredInvoices = useMemo(() => {
        let filtered = invoices;
        
        // Apply status filter
        if (activePaymentFilter !== 'all') {
            filtered = filtered.filter(invoice => {
                // Map invoice status to our filter categories
                const mappedStatus = (() => {
                    switch (invoice.status) {
                        case 'paid': return 'paid';
                        case 'draft':
                        case 'open': return 'pending';
                        case 'uncollectible':
                        case 'failed': return 'failed';
                        default: return invoice.status;
                    }
                })();
                
                return mappedStatus === activePaymentFilter;
            });
        }
        
        // Apply search filter if query exists
        if (searchQuery && searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(invoice => 
                (invoice.order_number && invoice.order_number.toLowerCase().includes(query)) ||
                (invoice.plan_name && invoice.plan_name.toLowerCase().includes(query)) ||
                (invoice.payment_method_brand && invoice.payment_method_brand.toLowerCase().includes(query))
            );
        }
        
        return filtered;
    }, [invoices, activePaymentFilter, searchQuery]);

    // Function to format date to readable format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    // Toggle sidebar - used by the top-left menu button
    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // UPDATED: Helper function to get auth token - for child components
    const getAuthToken = async () => {
        return await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email'
        });
    };

    // Render mobile invoice item
    const renderMobileInvoiceItem = (invoice, index) => {
        return (
            <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
            >
                <Box
                    sx={{
                        py: 2,
                        px: 2,
                        borderBottom: index < filteredInvoices.length - 1 ? '1px solid' : 'none',
                        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                        },
                    }}
                >
                    {/* Top row with invoice number and download button */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center">
                            <Avatar
                                sx={{
                                    bgcolor: 'rgba(255, 71, 117, 0.1)',
                                    color: '#FF4775',
                                    width: 36,
                                    height: 36,
                                    mr: 1.5
                                }}
                            >
                                <FileText size={18} />
                            </Avatar>
                            <Typography fontWeight={600} variant="body1">
                                {invoice.order_number}
                                {invoice.status === 'failed' && (
                                    <Box component="span" ml={1} sx={{ cursor: 'help' }}>
                                        <AlertTriangle size={14} color="#F44336" />
                                    </Box>
                                )}
                            </Typography>
                        </Box>
                        
                        {/* Download button */}
                        <DownloadButton
                            size="small"
                            onClick={() => handleDownloadInvoice(invoice)}
                            disabled={downloadingInvoices[invoice.order_number] || invoice.status === 'failed' || invoice.status === 'canceled'}
                            darkMode={darkMode}
                        >
                            {downloadingInvoices[invoice.order_number] ? (
                                <CircularProgress size={16} thickness={5} color="inherit" />
                            ) : (
                                <Download size={16} />
                            )}
                        </DownloadButton>
                    </Box>
                    
                    {/* Plan name and date */}
                    <Box ml={6.5} mb={1.5}>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            {invoice.plan_name || 'Subscription Payment'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {formatDate(invoice.created_at)}
                            {invoice.payment_method_last4 && invoice.payment_method_brand && (
                                <span> • {invoice.payment_method_brand.charAt(0).toUpperCase() + invoice.payment_method_brand.slice(1)} •••• {invoice.payment_method_last4}</span>
                            )}
                        </Typography>
                    </Box>
                    
                    {/* Bottom row with price and status */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" ml={6.5}>
                        <Typography variant="body2" fontWeight={700}>
                            ${typeof invoice.amount === 'number' ? invoice.amount.toFixed(2) : parseFloat(invoice.amount).toFixed(2)}
                        </Typography>
                        <AnimatedStatusBadge status={invoice.status} />
                    </Box>
                </Box>
            </motion.div>
        );
    };

    // Render desktop invoice item
    const renderDesktopInvoiceItem = (invoice, index) => {
        return (
            <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
            >
                <ListItem
                    sx={{
                        py: 2,
                        px: 2,
                        borderBottom: index < filteredInvoices.length - 1 ? '1px solid' : 'none',
                        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                        },
                    }}
                >
                    <ListItemAvatar>
                        <Avatar
                            sx={{
                                bgcolor: 'rgba(255, 71, 117, 0.1)',
                                color: '#FF4775'
                            }}
                        >
                            <FileText size={20} />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Box display="flex" alignItems="center">
                                <Typography fontWeight={600}>{invoice.order_number}</Typography>
                                {invoice.status === 'failed' && (
                                    <Tooltip title="Payment failed. Please update your payment method.">
                                        <Box component="span" ml={1} sx={{ cursor: 'help' }}>
                                            <AlertTriangle size={16} color="#F44336" />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        }
                        secondary={
                            <>
                                <Typography variant="body2" color="textSecondary">
                                    {invoice.plan_name || 'Subscription Payment'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {formatDate(invoice.created_at)}
                                    {invoice.payment_method_last4 && invoice.payment_method_brand && (
                                        <span> • {invoice.payment_method_brand.charAt(0).toUpperCase() + invoice.payment_method_brand.slice(1)} •••• {invoice.payment_method_last4}</span>
                                    )}
                                </Typography>
                            </>
                        }
                    />
                    
                    <Box 
                        sx={{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            ${typeof invoice.amount === 'number' ? invoice.amount.toFixed(2) : parseFloat(invoice.amount).toFixed(2)}
                        </Typography>
                        <AnimatedStatusBadge status={invoice.status} />
                        <Tooltip title="View & Download Invoice">
                            <IconButton 
                                size="small" 
                                onClick={() => handleDownloadInvoice(invoice)}
                                disabled={downloadingInvoices[invoice.order_number] || invoice.status === 'failed' || invoice.status === 'canceled'}
                                sx={{
                                    ml: 1,
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                    color: darkMode ? 'white' : 'inherit',
                                }}
                            >
                                {downloadingInvoices[invoice.order_number] ? (
                                    <CircularProgress size={18} thickness={5} />
                                ) : (
                                    <Download size={18} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </ListItem>
            </motion.div>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                backgroundColor: darkMode ? 'var(--background-dark)' : 'var(--background-light)',
            }}
        >
            {/* Sidebar - using the correct prop names that match EditPersonalInfo.js */}
            <Sidebar 
                activePage="payment"
                isOpen={sidebarOpen}
                onToggle={toggleSidebar}
                darkMode={darkMode}
            />

            {/* Main Content */}
            <Box
                className={`my-profile myprofile-main-content ${darkMode ? 'dark-mode' : ''}`}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    position: 'relative',
                    marginLeft: { xs: 0, sm: sidebarOpen ? '240px' : '80px' },
                    transition: 'margin-left 0.3s ease',
                    width: { xs: '100%', sm: `calc(100% - ${sidebarOpen ? 240 : 80}px)` },
                }}
            >
                <div className="noise-overlay" style={{ opacity: darkMode ? 0.18 : 0.12 }}></div>
                <BackgroundBlobs darkMode={darkMode} />

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: { xs: 2, sm: 3 },
                        width: '100%',
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            pt: { xs: 1, sm: 2 }
                        }}
                    >
                        {/* Mobile Header with Logo and Dark Mode Toggle */}
                        {isMobile && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                {/* Circledin text logo for mobile */}
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 700,
                                        background: 'linear-gradient(45deg, #FF4775, #FF7676)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        textShadow: '0 1px 3px rgba(255, 71, 117, 0.1)'
                                    }}
                                >
                                    Circledin
                                </Typography>
                                
                                {/* Dark mode toggle on mobile */}
                                <IconButton
                                    onClick={toggleDarkMode}
                                    aria-label="Toggle dark mode"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.03)',
                                        borderRadius: '12px',
                                        width: 48,
                                        height: 48,
                                    }}
                                >
                                    {darkMode ?
                                        <motion.div animate={{ rotate: 180 }} transition={{ duration: 0.5 }}>☀️</motion.div> :
                                        <motion.div animate={{ rotate: 0 }} transition={{ duration: 0.5 }}>🌙</motion.div>
                                    }
                                </IconButton>
                            </Box>
                        )}

                        {/* Payment Page Header - Improved Centered Design */}
                        <Box mb={isMobile ? 3 : 5} mt={isMobile ? 1 : 2}>
                            <Box 
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative'
                                }}
                            >
                                <Typography
                                    variant={isMobile ? "h5" : "h4"}
                                    component={motion.h4}
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="myprofile-text-gradient"
                                    fontWeight={700}
                                    sx={{ textAlign: 'center' }}
                                >
                                    Payment Center
                                </Typography>
                                <Typography
                                    variant="body1"
                                    component={motion.p}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 0.7 }}
                                    transition={{ delay: 0.3 }}
                                    color="textSecondary"
                                    sx={{ mt: 1, mb: 3, textAlign: 'center', px: 2 }}
                                >
                                    Manage your subscriptions, invoices, and payment methods
                                </Typography>
                                
                                {/* Dark mode toggle - desktop only, positioned appropriately */}
                                {!isMobile && (
                                    <Box 
                                        sx={{ 
                                            position: 'absolute', 
                                            top: 0,
                                            right: 0
                                        }}
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.4, type: "spring" }}
                                        >
                                            <IconButton
                                                onClick={toggleDarkMode}
                                                aria-label="Toggle dark mode"
                                                sx={{
                                                    background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                                    transition: 'all 0.3s ease',
                                                    width: 48,
                                                    height: 48,
                                                }}
                                            >
                                                {darkMode ?
                                                    <motion.div animate={{ rotate: 180 }} transition={{ duration: 0.5 }}>☀️</motion.div> :
                                                    <motion.div animate={{ rotate: 0 }} transition={{ duration: 0.5 }}>🌙</motion.div>
                                                }
                                            </IconButton>
                                        </motion.div>
                                    </Box>
                                )}
                            </Box>

                            {/* Tabs for different sections */}
                            <Box sx={{ mb: 2 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    variant={isMobile || isTablet ? "scrollable" : "standard"}
                                    scrollButtons={isMobile || isTablet ? "auto" : false}
                                    allowScrollButtonsMobile
                                    centered={!isMobile && !isTablet}
                                    sx={{
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: '#FF4775',
                                            height: '3px',
                                            borderRadius: '3px'
                                        },
                                        '& .MuiTabs-scrollButtons': {
                                            color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
                                        },
                                        '& .MuiTab-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                            minWidth: { xs: 'auto', sm: 120 },
                                            px: { xs: 1.5, sm: 2 },
                                            '&.Mui-selected': {
                                                color: '#FF4775'
                                            }
                                        }
                                    }}
                                >
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Receipt size={18} style={{ marginRight: '8px' }} />
                                                <span>Invoices</span>
                                            </Box>
                                        }
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CreditCard size={18} style={{ marginRight: '8px' }} />
                                                <span>Payment Methods</span>
                                            </Box>
                                        }
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <RefreshCw size={18} style={{ marginRight: '8px' }} />
                                                <span>Subscriptions</span>
                                            </Box>
                                        }
                                    />
                                </Tabs>
                            </Box>
                        </Box>

                        {/* Tab Panels */}
                        <AnimatePresence mode="wait">
                            {/* Invoices Tab Panel */}
                            {activeTab === 0 && (
                                <motion.div
                                    key="invoices"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Box sx={{ mb: 4 }}>
                                        {/* Invoices Filter and Search - Responsive Layout */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                justifyContent: 'space-between',
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                flexWrap: { sm: 'wrap' },
                                                mb: 3,
                                                gap: 2
                                            }}
                                        >
                                            {/* Filter Buttons with Responsive Wrapping */}
                                            <Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    gap: 1, 
                                                    flexWrap: 'wrap',
                                                    justifyContent: { xs: 'center', sm: 'flex-start' }
                                                }}
                                            >
                                                <Button
                                                    variant={activePaymentFilter === 'all' ? 'contained' : 'outlined'}
                                                    size="small"
                                                    onClick={() => handlePaymentFilterChange('all')}
                                                    sx={{
                                                        borderRadius: '20px',
                                                        textTransform: 'none',
                                                        backgroundColor: activePaymentFilter === 'all' ? '#FF4775' : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: activePaymentFilter === 'all' ? '#FF4775' : 'rgba(255, 71, 117, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    All
                                                </Button>
                                                <Button
                                                    variant={activePaymentFilter === 'paid' ? 'contained' : 'outlined'}
                                                    size="small"
                                                    onClick={() => handlePaymentFilterChange('paid')}
                                                    sx={{
                                                        borderRadius: '20px',
                                                        textTransform: 'none',
                                                        backgroundColor: activePaymentFilter === 'paid' ? '#4CAF50' : 'transparent',
                                                        borderColor: '#4CAF50',
                                                        color: activePaymentFilter === 'paid' ? 'white' : '#4CAF50',
                                                        '&:hover': {
                                                            backgroundColor: activePaymentFilter === 'paid' ? '#4CAF50' : 'rgba(76, 175, 80, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    Paid
                                                </Button>
                                                <Button
                                                    variant={activePaymentFilter === 'pending' ? 'contained' : 'outlined'}
                                                    size="small"
                                                    onClick={() => handlePaymentFilterChange('pending')}
                                                    sx={{
                                                        borderRadius: '20px',
                                                        textTransform: 'none',
                                                        backgroundColor: activePaymentFilter === 'pending' ? '#FFC107' : 'transparent',
                                                        borderColor: '#FFC107',
                                                        color: activePaymentFilter === 'pending' ? 'white' : '#FFC107',
                                                        '&:hover': {
                                                            backgroundColor: activePaymentFilter === 'pending' ? '#FFC107' : 'rgba(255, 193, 7, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    Pending
                                                </Button>
                                                <Button
                                                    variant={activePaymentFilter === 'failed' ? 'contained' : 'outlined'}
                                                    size="small"
                                                    onClick={() => handlePaymentFilterChange('failed')}
                                                    sx={{
                                                        borderRadius: '20px',
                                                        textTransform: 'none',
                                                        backgroundColor: activePaymentFilter === 'failed' ? '#F44336' : 'transparent',
                                                        borderColor: '#F44336',
                                                        color: activePaymentFilter === 'failed' ? 'white' : '#F44336',
                                                        '&:hover': {
                                                            backgroundColor: activePaymentFilter === 'failed' ? '#F44336' : 'rgba(244, 67, 54, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    Failed
                                                </Button>
                                            </Box>

                                            {/* Search Field - Full width on mobile */}
                                            <TextField
                                                placeholder="Search invoices..."
                                                size="small"
                                                variant="outlined"
                                                fullWidth={isMobile}
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Search size={18} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        borderRadius: '20px',
                                                        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                                                    }
                                                }}
                                                sx={{ 
                                                    width: { xs: '100%', sm: 250 },
                                                    maxWidth: '100%'
                                                }}
                                            />
                                        </Box>

                                        {/* Invoices List */}
                                        <GlassCard darkMode={darkMode}>
                                            {loading ? (
                                                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                                                    <CircularProgress size={40} sx={{ color: '#FF4775' }} />
                                                </Box>
                                            ) : error ? (
                                                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                                                    <Typography color="error">{error}</Typography>
                                                </Box>
                                            ) : filteredInvoices.length > 0 ? (
                                                <Box>
                                                    {/* Use different layouts for mobile and desktop */}
                                                    {isMobile ? (
                                                        // Mobile layout
                                                        filteredInvoices.map((invoice, index) => 
                                                            renderMobileInvoiceItem(invoice, index)
                                                        )
                                                    ) : (
                                                        // Desktop layout
                                                        <List disablePadding>
                                                            {filteredInvoices.map((invoice, index) => 
                                                                renderDesktopInvoiceItem(invoice, index)
                                                            )}
                                                        </List>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
                                                    <Box
                                                        sx={{
                                                            width: 70,
                                                            height: 70,
                                                            borderRadius: '50%',
                                                            background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mb: 2
                                                        }}
                                                    >
                                                        <Receipt size={30} color={darkMode ? "#A7ABBE" : "#767991"} />
                                                    </Box>
                                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                                        No invoices found
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" textAlign="center">
                                                        There are no invoices matching your filter criteria.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </GlassCard>
                                    </Box>
                                </motion.div>
                            )}

                            {/* Payment Methods Tab Panel */}
                            {activeTab === 1 && (
                                <motion.div
                                    key="payment-methods"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <PaymentMethods
                                        darkMode={darkMode}
                                        // UPDATED: Pass the payment service and auth function
                                        service={paymentService}
                                        getAuthToken={getAuthToken}
                                    />
                                </motion.div>
                            )}

                            {/* Subscriptions Tab Panel */}
                            {activeTab === 2 && (
                                <motion.div
                                    key="subscriptions"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Subscriptions
                                        darkMode={darkMode}
                                        // UPDATED: Pass the payment service and auth function
                                        service={paymentService}
                                        getAuthToken={getAuthToken}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Container>
                </Box>

                {/* Notification Snackbar - Adjusted position */}
                <Snackbar 
                    open={notification.open} 
                    autoHideDuration={6000} 
                    onClose={handleCloseNotification}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    sx={{ mb: 2 }}
                >
                    <Alert 
                        onClose={handleCloseNotification} 
                        severity={notification.severity}
                        sx={{ 
                            width: '100%',
                            borderRadius: '12px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default Payment;